import { Loader, Modal, NumberInput } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { Button, InputField } from '@xver/components/Atoms';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePricingQuery } from '@xver/hooks/usePricingQuery';

import { useVolumeMutations } from '../hooks/useVolumeMutations';
import { formatBaht, resolveVolumeRate, volumeGB, volumeMonthlyCost } from '../utils/volumeCost';
import {
    MAX_VOLUME_GIB,
    MIN_VOLUME_GIB,
    validateMountPath,
    validateVolumeName,
    validateVolumeSize,
    VOLUME_ERROR_DEFAULTS,
    type VolumeErrorKey,
} from '../utils/volumeValidation';

interface AddVolumeModalProps {
    containerId: string;
    opened: boolean;
    onClose: () => void;
}

const QUICK_SIZES = [10, 20, 50, 100];
const DEFAULT_SIZE = 10;

const NUMBER_INPUT_STYLES = {
    input: {
        backgroundColor: 'var(--surface-2)',
        borderColor: 'var(--border)',
        color: 'var(--fg)',
    },
};

export default function AddVolumeModal({ containerId, opened, onClose }: AddVolumeModalProps) {
    const { t } = useTranslation('containerEngineInstance');
    const { createVolume } = useVolumeMutations(containerId);
    const { data: pricing } = usePricingQuery();
    const volumeRate = resolveVolumeRate(pricing?.products.container.rates.volume_gb_monthly_rate);

    const [name, setName] = useState('');
    const [mountPath, setMountPath] = useState('');
    const [sizeGiB, setSizeGiB] = useState<number>(DEFAULT_SIZE);
    const [touched, setTouched] = useState({ name: false, mountPath: false, sizeGiB: false });

    useEffect(() => {
        if (!opened) return;
        setName('');
        setMountPath('');
        setSizeGiB(DEFAULT_SIZE);
        setTouched({ name: false, mountPath: false, sizeGiB: false });
    }, [opened]);

    const message = (key: VolumeErrorKey | null) =>
        key ? t(`volumes.validation.${key}`, VOLUME_ERROR_DEFAULTS[key]) : undefined;

    const nameError = validateVolumeName(name);
    const mountPathError = validateMountPath(mountPath);
    const sizeError = validateVolumeSize(sizeGiB);
    const isValid = !nameError && !mountPathError && !sizeError;
    const isSubmitting = createVolume.isPending;

    const handleSubmit = (event?: FormEvent) => {
        event?.preventDefault();
        setTouched({ name: true, mountPath: true, sizeGiB: true });
        if (!isValid || isSubmitting) return;

        createVolume.mutate(
            { name: name.trim(), mount_path: mountPath.trim(), size_gib: sizeGiB },
            { onSuccess: onClose }
        );
    };

    return (
        <Modal
            size="md"
            opened={opened}
            onClose={isSubmitting ? () => {} : onClose}
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <form onSubmit={handleSubmit} className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">
                            {t('volumes.addTitle', 'Add volume')}
                        </span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">
                        {t(
                            'volumes.addDescription',
                            'Create a persistent volume and mount it into this container.'
                        )}
                    </p>
                </div>

                <div className="w-full flex flex-col justify-start items-start gap-4">
                    <InputField
                        size="md"
                        width="100%"
                        label={t('volumes.nameLabel', 'Name')}
                        description={t(
                            'volumes.nameDescription',
                            'Lowercase letters, digits and hyphens only.'
                        )}
                        placeholder={t('volumes.namePlaceholder', 'data')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                        error={touched.name ? message(nameError) : undefined}
                        disabled={isSubmitting}
                        autoFocus
                    />

                    <InputField
                        size="md"
                        width="100%"
                        label={t('volumes.mountPathLabel', 'Mount path')}
                        description={t(
                            'volumes.mountPathDescription',
                            'Absolute path inside the container where the volume is mounted.'
                        )}
                        placeholder={t('volumes.mountPathPlaceholder', '/data')}
                        value={mountPath}
                        onChange={(e) => setMountPath(e.target.value)}
                        onBlur={() => setTouched((prev) => ({ ...prev, mountPath: true }))}
                        error={touched.mountPath ? message(mountPathError) : undefined}
                        disabled={isSubmitting}
                    />

                    <div className="w-full flex flex-col gap-2">
                        <NumberInput
                            size="md"
                            label={t('volumes.sizeLabel', 'Size (GiB)')}
                            description={t(
                                'volumes.sizeDescription',
                                'Between 1 and 1024 GiB. A volume can be grown later, but never shrunk.'
                            )}
                            min={MIN_VOLUME_GIB}
                            max={MAX_VOLUME_GIB}
                            clampBehavior="none"
                            allowDecimal={false}
                            allowNegative={false}
                            value={sizeGiB}
                            onChange={(value) => setSizeGiB(Number(value))}
                            onBlur={() => setTouched((prev) => ({ ...prev, sizeGiB: true }))}
                            error={touched.sizeGiB ? message(sizeError) : undefined}
                            disabled={isSubmitting}
                            styles={NUMBER_INPUT_STYLES}
                        />
                        <p className="text-xs text-fg/68">
                            {t('volumes.estimatedMonthly', {
                                amount: formatBaht(volumeMonthlyCost(sizeGiB, volumeRate)),
                                gb: volumeGB(sizeGiB).toFixed(2),
                                defaultValue: '≈ {{amount}} ฿ / month ({{gb}} GB)',
                            })}
                        </p>
                        <div
                            className="flex flex-row flex-wrap items-center gap-2"
                            role="group"
                            aria-label={t('volumes.quickPick', 'Common sizes')}
                        >
                            {QUICK_SIZES.map((size) => (
                                <button
                                    key={size}
                                    type="button"
                                    disabled={isSubmitting}
                                    aria-pressed={sizeGiB === size}
                                    onClick={() => {
                                        setSizeGiB(size);
                                        setTouched((prev) => ({ ...prev, sizeGiB: true }));
                                    }}
                                    className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors disabled:opacity-50 ${
                                        sizeGiB === size
                                            ? 'border-fg/40 bg-fg/10 text-fg'
                                            : 'border-fg/15 text-fg/68 hover:bg-fg/8'
                                    }`}
                                >
                                    {t('volumes.sizeValue', { size, defaultValue: '{{size}} GiB' })}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="w-full flex flex-row items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10">
                        <IconAlertTriangle
                            size={18}
                            className="shrink-0 text-amber-400 light:text-amber-800"
                        />
                        <p className="text-xs text-amber-200/90 light:text-amber-800">
                            {t(
                                'volumes.redeployWarning',
                                'Adding a volume redeploys the container. Expect a short interruption.'
                            )}
                        </p>
                    </div>

                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button width="100%" type="submit" disabled={!isValid || isSubmitting}>
                            {isSubmitting ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('volumes.submitting', 'Adding…')}</span>
                                </div>
                            ) : (
                                t('volumes.submit', 'Add volume')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            {t('volumes.cancel', 'Cancel')}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
